import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import { BlockingCategory } from './WritingAssistMoments.types';
import { GuidelineList } from './WritingAssistMoments.styles';
import usePrefersReducedMotion from './usePrefersReducedMotion';

export interface CommunityGuidelinesModalProps {
  open: boolean;
  category: BlockingCategory;
  onClose: () => void;
}

type GuidelineId = 'contact' | 'booking' | 'services' | 'professional';

const ACTIVE_GUIDELINE: Record<BlockingCategory, GuidelineId> = {
  offsite_steering: 'booking',
  competitor: 'services',
  disparagement: 'professional',
  toxic: 'professional',
};

const CommunityGuidelinesModal: React.FC<CommunityGuidelinesModalProps> = ({
  open,
  category,
  onClose,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const active = ACTIVE_GUIDELINE[category];
  const refs = useRef<Record<GuidelineId, HTMLLIElement | null>>({
    contact: null,
    booking: null,
    services: null,
    professional: null,
  });
  const rules = useMemo(() => ([
    {
      id: 'contact' as const,
      copy: 'Keep contact off the page. Phone numbers, emails, links and handles are removed automatically — connecting happens through TempStars, which keeps every booking protected and fair.',
    },
    {
      id: 'booking' as const,
      copy: "Book through the platform. Posts that arrange work or payment outside TempStars can't be published.",
    },
    {
      id: 'services' as const,
      copy: 'No ads for other services. Profiles and postings are for you and your work — not for promoting other staffing or hiring platforms.',
    },
    {
      id: 'professional' as const,
      copy: "Keep it professional. No toxic, offensive or demeaning language — and public pages aren't the place to air grievances (our support team is).",
    },
  ]), []);

  const focusActiveRule = useCallback(() => {
    const target = refs.current[active];
    if (target) {
      target.focus({ preventScroll: true });
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
      }
    }
  }, [active, reducedMotion]);

  useEffect(() => {
    if (open) focusActiveRule();
  }, [focusActiveRule, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="writing-guidelines-title"
      transitionDuration={reducedMotion ? 0 : { enter: 150, exit: 0 }}
      TransitionProps={{ onEntered: focusActiveRule }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="writing-guidelines-title">The house rules for what you post</DialogTitle>
      <DialogContent>
        <p>TempStars runs on trust between offices and dental professionals. A few rules keep it that way:</p>
        <GuidelineList>
          {rules.map(rule => (
            <li
              key={rule.id}
              ref={(node) => { refs.current[rule.id] = node; }}
              aria-current={rule.id === active ? 'true' : undefined}
              tabIndex={rule.id === active ? -1 : undefined}
            >
              {rule.copy}
            </li>
          ))}
        </GuidelineList>
        <p>To keep these rules working for everyone, content posted to profiles, cover letters and job postings is automatically reviewed by AI when you save it. Nothing is blocked without showing you why.</p>
        <p>
          Questions or something feels wrong?{' '}
          <a href="mailto:help@tempstars.com">Contact support</a>
          {' '}— we read everything.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default CommunityGuidelinesModal;
